const MARKER_RE = /\{\{([^=}]+)=([^}]*)\}\}/g;

/**
 * Extract every COMPLETE `{{key=value}}` control marker from an accumulated
 * stream buffer, in the order they appear. Values are URL-decoded.
 *
 * The buffer grows monotonically as the stream arrives, so the returned list
 * only ever grows at its tail — callers can safely apply "only the new ones"
 * by tracking how many they have already consumed.
 *
 * @returns {Array<{ key: string, value: string }>}
 */
export function extractCpaMarkers(buffer) {
    const out = [];
    if (!buffer) return out;
    let match;
    MARKER_RE.lastIndex = 0;
    while ((match = MARKER_RE.exec(buffer)) !== null) {
        let value = match[2];
        try { value = decodeURIComponent(match[2]); } catch (_) { value = match[2]; }
        out.push({ key: match[1], value });
    }
    MARKER_RE.lastIndex = 0;
    return out;
}

/**
 * Strip all control markers (complete `{{key=value}}` and any trailing partial
 * `{{key=val` that has not closed yet) from the accumulated buffer so only the
 * human-readable AI prose remains.
 */
export function stripCpaMarkers(text) {
    if (!text) return '';
    return text
        .replace(/\{\{[^}]*\}\}/g, '') // complete markers
        .replace(/\{\{[^}]*$/g, '')    // trailing partial marker (still streaming)
        .trim();
}

export function applyCpaMarkers(prev, markers) {
    const next = { ...prev };
    Object.entries(markers).forEach(([key, value]) => {
        switch (key) {
            case 'session_id':
                next.sessionId = value;
                break;
            case 'session_resumed':
                next.sessionResumed = value === 'true';
                break;
            case 'show_form':
                next.currentForm = value;
                next.errorCode = null;
                // Once we advance to the contact form, the quote decision is made —
                // hide the Yes/No quote prompt so it can't be clicked again.
                if (value === 'contact_form') next.showQuotePrompt = false;
                break;
            case 'org_type_saved':
                next.orgTypeSaved = true;
                next.currentForm = null;
                break;
            case 'goals_saved':
                next.goalsSaved = true;
                next.currentForm = null;
                next.showPackagePanel = true;
                break;
            case 'recommended_package':
                next.recommendedPackage = value;
                next.showPackagePanel = true;
                break;
            case 'show_quote_prompt':
                next.showQuotePrompt = true;
                next.showPackagePanel = true;
                break;
            case 'contact_form_saved':
                next.contactFormSaved = true;
                next.currentForm = null;
                break;
            case 'application_submitted':
                next.applicationSubmitted = true;
                next.showQuotePrompt = false;
                next.currentForm = null;
                break;
            case 'application_code':
                next.applicationCode = value;
                break;
            case 'application_complete':
                next.applicationComplete = true;
                next.showQuotePrompt = false;
                break;
            case 'application_already_submitted':
                next.applicationAlreadySubmitted = true;
                break;
            case 'error_code':
                next.errorCode = value;
                break;
            default:
                break;
        }
    });
    return next;
}

export const CPA_INITIAL_UI_STATE = {
    sessionId: null,
    sessionResumed: false,
    currentForm: null,
    orgTypeSaved: false,
    goalsSaved: false,
    showQuotePrompt: false,
    contactFormSaved: false,
    applicationSubmitted: false,
    applicationCode: null,
    applicationComplete: false,
    applicationAlreadySubmitted: false,
    errorCode: null,
    showPackagePanel: false,
    recommendedPackage: null,
};

export function parseMarkdownSimple(text) {
    if (!text) return '';
    let f = text;
    f = f.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    f = f.replace(/\n/g, '<br/>');
    return f;
}
